import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import {
  EntityType,
  EntityTypesSupportingRelationshipBulkEdit,
  Person,
} from "@mapmycustomers/shared/types/entity";
import { Company } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import { fetchCompanies } from "@app/store/company/actions";
import { fetchPerson } from "@app/store/person/actions";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";

import styles from "./RelationshipField.module.scss";

const messages = defineMessages({
  changeTo: {
    id: "view.selectBar.button.bulkEdit.panel.changeTo.entity",
    defaultMessage: "Change {entity} to",
    description: "Change to entity type",
  },
  openModal: {
    id: "view.selectBar.button.bulkEdit.panel.openModal",
    defaultMessage: "Open {entity, select, accounts {Companies} contacts {People} other {}}",
    description: "Open modal button title",
  },
  selectParentCompany: {
    id: "view.selectBar.button.bulkEdit.panel.selectParentCompany",
    defaultMessage: "Select Parent Company",
    description: "Title of a Select Parent modal in Bulk edit",
  },
});

export type EntitiesSupportingRelationshipBulkEdit = Company | Company[] | Person | undefined;

const companyFieldNames: string[] = [
  CompanyFieldName.PARENT_COMPANY,
  DealFieldName.COMPANY,
  PersonFieldName.COMPANIES,
];

interface Props {
  entityType: EntityTypesSupportingRelationshipBulkEdit;
  field: IField;
  onChange: (value?: any) => void;
  onChangePrimaryEntityId?: (id: number) => void;
  onFetchCompanies: typeof fetchCompanies;
  onFetchPerson: typeof fetchPerson;
  value: EntitiesSupportingRelationshipBulkEdit;
}

const RelationshipField: React.FC<Props> = ({
  entityType,
  field,
  onChange,
  onChangePrimaryEntityId,
  onFetchCompanies,
  onFetchPerson,
  value,
}) => {
  const intl = useIntl();
  const [modalVisible, showModal, hideModal] = useBoolean();
  const [primaryCompanyId, setPrimaryCompanyId] = useState<Person["primaryAccountId"]>();
  const fieldName = field.name;

  const handleSelectCompanies = useCallback(
    (selectedCompaniesIds: Company["id"][]) => {
      if (selectedCompaniesIds.length > 0) {
        onFetchCompanies({
          callback: (companies: Company[]) => {
            onChange(entityType === EntityType.PERSON ? companies : companies[0]);
            if (primaryCompanyId) {
              onChangePrimaryEntityId?.(primaryCompanyId);
            }
          },
          ids: selectedCompaniesIds,
        });
      }
    },
    [entityType, onChange, onChangePrimaryEntityId, onFetchCompanies, primaryCompanyId]
  );

  const handleSelectPeople = useCallback(
    (selectedPeopleIds: Person["id"][]) => {
      const personId = selectedPeopleIds[0];
      onFetchPerson({
        id: personId,
        callback: (person: Person) => {
          onChange(person);
        },
        options: { includeAccessStatus: true, includeUsersWithAccess: true },
      });
    },
    [onChange, onFetchPerson]
  );

  return (
    <div className={styles.container}>
      <div>
        <span className={styles.changeTo}>
          {intl.formatMessage(messages.changeTo, { entity: field.displayName })}
        </span>
        {value && (
          <div className={styles.list}>
            {Array.isArray(value)
              ? value?.map((company) => <span key={company.id}>{company.name}</span>)
              : value.name}
          </div>
        )}
      </div>

      {companyFieldNames.includes(fieldName) && modalVisible && (
        <CompanyAssociation
          allowAdd={false}
          assignedCompanies={value ? (Array.isArray(value) ? value : [value]) : []}
          entityType={entityType}
          multiselect={entityType === EntityType.PERSON}
          onHide={hideModal}
          onSelect={handleSelectCompanies}
          onUpdatePrimaryCompany={setPrimaryCompanyId}
          primaryCompanyId={primaryCompanyId ?? undefined}
          supportsPrimaryEntity={entityType === EntityType.PERSON}
          title={
            entityType === EntityType.COMPANY
              ? intl.formatMessage(messages.selectParentCompany)
              : undefined
          }
        />
      )}

      {field.name === DealFieldName.PERSON && modalVisible && (
        <PeopleAssociation
          allowAdd={false}
          assignedPeople={value ? [value as Person] : undefined}
          multiselect={false}
          onHide={hideModal}
          onSelect={handleSelectPeople}
        />
      )}
      <div>
        <Button onClick={showModal}>
          {intl.formatMessage(messages.openModal, {
            entity: fieldName === DealFieldName.PERSON ? EntityType.PERSON : EntityType.COMPANY,
          })}
        </Button>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onFetchCompanies: fetchCompanies,
  onFetchPerson: fetchPerson,
};

export default connect(null, mapDispatchToProps)(RelationshipField);
